import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { usePageMeta } from "@/hooks/usePageMeta";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db7k03atp9sllopf7gs0";

const contactItems = [
  {
    icon: "ri-mail-line",
    label: "联系邮箱",
    value: "support@yellowvideohub.com",
  },
  {
    icon: "ri-time-line",
    label: "服务时间",
    value: "周一至周五 09:00 - 18:00",
  },
  {
    icon: "ri-map-pin-line",
    label: "内容合作",
    value: "欢迎优质内容与资源方联系",
  },
];

export default function ContactPage() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  usePageMeta(
    "联系我们 - 黄色免费视频",
    "联系黄色免费视频团队，反馈内容问题、资源合作或使用建议，可通过留言表单或邮箱与我们取得联系。",
    "联系我们,黄色免费视频,内容合作,反馈建议",
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() === "") return;
      payload.append(key, String(value));
    });

    setStatus("loading");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null =
        null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = /spam/i.test(serverMsg);

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查网络后重试。");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full px-4 md:px-6 pt-6 md:pt-8">
          <nav className="flex items-center gap-1.5 text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors">
              首页
            </Link>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line" />
            </span>
            <span className="text-foreground-800 font-medium">联系我们</span>
          </nav>

          <div className="mt-4 max-w-2xl">
            <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-foreground-950">
              联系我们
            </h1>
            <p className="mt-2 text-sm md:text-base text-foreground-600">
              如果你对网站内容、资源合作或使用体验有任何建议，欢迎通过下面的表单或邮箱与我们联系，我们会尽快回复。
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-8 md:py-10 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          <div className="lg:col-span-2">
            <div className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-7">
              <h2 className="font-heading text-lg font-bold text-foreground-950 mb-5">
                发送留言
              </h2>

              {status === "success" ? (
                <div className="flex flex-col items-center text-center py-8">
                  <span className="w-16 h-16 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mb-4">
                    <i className="ri-checkbox-circle-line text-3xl" />
                  </span>
                  <h3 className="font-heading text-xl font-bold text-foreground-950">
                    留言已提交
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600 max-w-sm">
                    感谢你的反馈，我们已经收到你的信息，会尽快与你联系。
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    再写一条
                  </button>
                </div>
              ) : (
                <form
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label
                        htmlFor="contact-name"
                        className="block text-sm font-medium text-foreground-800 mb-1.5"
                      >
                        姓名
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        placeholder="请输入你的姓名"
                        className="w-full h-11 px-4 text-sm bg-background-50 border border-background-200 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="contact-email"
                        className="block text-sm font-medium text-foreground-800 mb-1.5"
                      >
                        邮箱
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        placeholder="请输入你的邮箱"
                        className="w-full h-11 px-4 text-sm bg-background-50 border border-background-200 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="contact-message"
                      className="block text-sm font-medium text-foreground-800 mb-1.5"
                    >
                      留言内容
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={6}
                      placeholder="请描述你的问题或建议（不超过 500 字）"
                      className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 resize-none"
                    />
                  </div>

                  <input
                    className="form-guard-field"
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {status === "error" && (
                    <p className="flex items-start gap-2 text-sm text-primary-700">
                      <span className="w-4 h-4 mt-0.5 flex items-center justify-center">
                        <i className="ri-error-warning-line" />
                      </span>
                      {formError}
                    </p>
                  )}

                  <div className="flex items-center gap-3">
                    <button
                      type="submit"
                      disabled={status === "loading"}
                      className="inline-flex items-center gap-2 h-11 px-7 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {status === "loading" ? (
                        <>
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-loader-4-line animate-spin" />
                          </span>
                          提交中...
                        </>
                      ) : (
                        <>
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-send-plane-line" />
                          </span>
                          提交留言
                        </>
                      )}
                    </button>
                    <span className="text-xs text-foreground-500">
                      我们仅将你的信息用于回复本次咨询
                    </span>
                  </div>
                </form>
              )}
            </div>
          </div>

          <aside className="lg:col-span-1 space-y-4">
            {contactItems.map((item) => (
              <div
                key={item.label}
                className="rounded-lg bg-background-100 border border-background-200 p-5"
              >
                <span className="w-11 h-11 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center mb-3">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wide">
                  {item.label}
                </p>
                <p className="mt-1 text-sm font-medium text-foreground-900 break-words">
                  {item.value}
                </p>
              </div>
            ))}

            <div className="rounded-lg bg-secondary-100 border border-secondary-200 p-5">
              <h3 className="font-heading text-base font-bold text-secondary-950 mb-2">
                温馨提示
              </h3>
              <p className="text-sm text-secondary-900 leading-relaxed">
                本站为视频资源聚合展示平台，不提供视频上传服务。如需反馈内容相关的问题，请在留言中附上视频标题或链接，便于我们定位。
              </p>
            </div>
          </aside>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}